import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  Trophy,
  Users,
  Search,
  Check,
  X,
  CheckCheck,
  XCircle,
  Minus,
  Globe2
} from 'lucide-react';
import DetailLayout from './layouts/DetailLayout';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Input
} from './ui';
import PlayerAvatar from './ui/PlayerAvatar';
import Modal from './ui/Modal';
import { useAuth } from '../hooks/useAuth';
import { useModal } from '../hooks/useModal';
import {
  getGroupById,
  getGroupPlayers,
  getJuegoById,
  getAsistenciasByJuego,
  saveAsistenciaJuego,
  updateJuego
} from '../lib/supabase';
import { parseLocalDate, getMexicoDateString } from '../lib/dateUtils';
import JuegoScoreModal from './torneos/JuegoScoreModal';
import { isActiveGroupRelation, isVisiblePlayer } from '../lib/playerVisibility';
import { useOrganization } from '../contexts/OrganizationContext';
import { puedeEditarJuego } from '../lib/torneoOrigen';

const STATUS_LABELS = {
  programado: 'Programado',
  en_curso: 'En curso',
  terminado: 'Terminado',
  cancelado: 'Cancelado',
  pospuesto: 'Pospuesto'
};

const STATUS_COLORS = {
  programado: 'bg-blue-50 text-blue-700 border-blue-200',
  en_curso: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  terminado: 'bg-slate-50 text-slate-700 border-slate-200',
  cancelado: 'bg-rose-50 text-rose-700 border-rose-200',
  pospuesto: 'bg-amber-50 text-amber-700 border-amber-200'
};

const GameAttendanceScreen = () => {
  const { groupId, juegoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { coach } = useAuth();
  const { org } = useOrganization();
  const { modal, showError, showSuccess, closeModal } = useModal();

  const [group, setGroup] = useState(location.state?.group || null);
  const [juego, setJuego] = useState(location.state?.juego || null);
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const focusTarget = location.state?.focusSection;

  // Terminar/reactivar el juego, editar el marcador (por boton o por este mismo
  // focusTarget) son solo del torneo interno: en un juego que baja del rol oficial de
  // la red, el estado y el marcador los pone la organizadora y la base rechaza la
  // escritura del club. Se calcula temprano porque el efecto de foco de abajo tambien
  // la necesita, no solo los botones.
  const esMio = puedeEditarJuego(juego, org?.id);

  const loadGameData = useCallback(async () => {
    if (!groupId || !juegoId) return;
    try {
      setLoading(true);

      if (!group) {
        const { data, error } = await getGroupById(groupId);
        if (error) throw error;
        setGroup(data);
      }

      const juegoData = await getJuegoById(juegoId);
      setJuego(juegoData);

      const { data: playersData, error: playersError } = await getGroupPlayers(groupId);
      if (playersError) throw playersError;

      const processedPlayers = [];
      const seen = new Set();
      (playersData || []).forEach((rel) => {
        const player = rel.jugadores;
        if (!player) return;
        if (!isVisiblePlayer(player)) return;
        if (!isActiveGroupRelation(rel)) return;
        if (!seen.has(player.id)) {
          seen.add(player.id);
          processedPlayers.push(player);
        }
      });
      setPlayers(processedPlayers);

      const asistenciaRecords = await getAsistenciasByJuego(juegoId);
      const attendanceMap = {};
      const recordedIds = new Set();

      (asistenciaRecords || []).forEach((record) => {
        if (!record.jugador_id) return;
        attendanceMap[record.jugador_id] = record.presente;
        recordedIds.add(record.jugador_id);
      });

      processedPlayers.forEach((player) => {
        if (!recordedIds.has(player.id)) {
          attendanceMap[player.id] = null;
        }
      });

      setAttendance(attendanceMap);
    } catch (error) {
      console.error('Error loading game attendance:', error);
      showError('Error', 'No pudimos cargar la información del juego o sus jugadores.');
    } finally {
      setLoading(false);
    }
  }, [groupId, juegoId, group, showError]);

  useEffect(() => {
    loadGameData();
  }, [loadGameData]);

  useEffect(() => {
    // Igual de protegido que el boton que abre el mismo modal: un state manipulado o
    // un futuro llamador con focusSection: 'resultado' no debe abrir el marcador en un
    // juego ajeno. Mientras juego todavia carga, puedeEditarJuego(null, ...) ya da
    // false, asi que esta ventana tambien queda cerrada.
    if (focusTarget === 'resultado' && esMio) {
      setShowScoreModal(true);
    }
  }, [focusTarget, esMio]);

  useEffect(() => {
    if (!juego) return;
  }, [juego?.id]);

  const filteredPlayers = useMemo(() => {
    if (!searchTerm) return players;
    const term = searchTerm.toLowerCase();
    return players.filter((player) => {
      const nameMatch = player.nombre_completo?.toLowerCase().includes(term);
      const nickMatch = player.nickname?.toLowerCase().includes(term);
      const numberMatch = player.numero ? String(player.numero).includes(term) : false;
      return nameMatch || nickMatch || numberMatch;
    });
  }, [players, searchTerm]);

  const stats = useMemo(() => {
    const summary = {
      presente: 0,
      retardo: 0,
      ausente: 0,
      exento: 0,
      pendientes: 0
    };

    players.forEach((player) => {
      const state = attendance[player.id];
      if (!state) {
        summary.pendientes += 1;
      } else if (state === 'exento') {
        summary.exento += 1;
      } else if (summary[state] !== undefined) {
        summary[state] += 1;
      }
    });

    return summary;
  }, [attendance, players]);

  const updateAttendance = async (playerIds, state) => {
    if (!playerIds.length || !juego) return;

    const previous = {};
    playerIds.forEach((id) => {
      previous[id] = attendance[id] ?? null;
    });

    setAttendance((prev) => {
      const updated = { ...prev };
      playerIds.forEach((id) => {
        updated[id] = state;
      });
      return updated;
    });

    try {
      setSaving(true);
      const descripcion =
        juego.descripcion ||
        juego.nombre ||
        `Juego del grupo ${group?.nombre_del_grupo || ''}`.trim();
      const staffId = coach?.rol === 'admin' ? null : coach?.id || null;

      const payload = playerIds.map((playerId) => ({
        jugador_id: playerId,
        grupo_id: groupId,
        juego_id: juego.id,
        fecha: juego.fecha || getMexicoDateString(),
        presente: state,
        descripcion,
        staff_id: staffId
      }));

      await saveAsistenciaJuego(payload);
    } catch (error) {
      console.error('Error guardando asistencia del juego:', error);
      showError('Error al guardar', 'No se pudo guardar la asistencia. Intenta nuevamente.');
      setAttendance((prev) => {
        const reverted = { ...prev };
        playerIds.forEach((id) => {
          reverted[id] = previous[id];
        });
        return reverted;
      });
    } finally {
      setSaving(false);
    }
  };

  const cycleAttendance = (playerId) => {
    const currentState = attendance[playerId] || null;
    let nextState;

    switch (currentState) {
      case 'presente':
        nextState = 'retardo';
        break;
      case 'retardo':
        nextState = 'ausente';
        break;
      case 'ausente':
        nextState = 'exento';
        break;
      case 'exento':
        nextState = 'presente';
        break;
      default:
        nextState = 'presente';
    }

    updateAttendance([playerId], nextState);
  };

  const markAll = (state) => {
    const ids = players.map((player) => player.id);
    updateAttendance(ids, state);
  };

  const finishGameAttendance = async () => {
    if (!juego || finishing) return;
    // Defensa en profundidad: la proteccion de render (el boton vive dentro de
    // {esMio && ...}) no debe ser la unica barrera. Si algun dia un boton se cuela
    // fuera de ese bloque, este guard sigue rechazando la escritura.
    if (!esMio) return;
    try {
      setFinishing(true);
      const pendingPlayers = players
        .filter((player) => !attendance[player.id])
        .map((player) => player.id);

      if (pendingPlayers.length) {
        await updateAttendance(pendingPlayers, 'ausente');
      }

      await updateJuego(juego.id, { estatus: 'terminado' });
      setJuego((prev) => (prev ? { ...prev, estatus: 'terminado' } : prev));
      showSuccess('Juego terminado', 'Se cerró el registro de asistencia para este partido.');
    } catch (error) {
      console.error('Error finishing game:', error);
      showError('Error', 'No se pudo terminar el juego. Intenta nuevamente.');
    } finally {
      setFinishing(false);
    }
  };

  const reactivateGame = async () => {
    if (!juego) return;
    // Misma defensa en profundidad que finishGameAttendance.
    if (!esMio) return;
    try {
      await updateJuego(juego.id, { estatus: 'en_curso' });
      setJuego((prev) => (prev ? { ...prev, estatus: 'en_curso' } : prev));
      showSuccess('Juego reactivado', 'Ahora puedes editar la asistencia nuevamente.');
    } catch (error) {
      console.error('Error reactivating game:', error);
      showError('Error', 'No se pudo reactivar el juego.');
    }
  };

  const statusKey = (juego?.estatus || 'programado').toLowerCase();
  const isEditable = !['terminado', 'cancelado'].includes(statusKey);
  const canEditResultado = statusKey === 'terminado';
  const opponent = juego?.equipo?.nombre || juego?.equipo_nombre || 'Rival por definir';
  const torneoNombre = juego?.torneo?.nombre;

  const handleScoreSaved = (updated) => {
    setJuego((prev) => ({ ...(prev || {}), ...updated }));
    showSuccess('Resultado guardado', 'El marcador se actualizó correctamente.');
  };

  if (loading || !juego) {
    return (
      <DetailLayout
        title="Asistencia de juego"
        breadcrumbs={[
          { label: 'Grupos', path: '/groups' },
          { label: group?.nombre_del_grupo || 'Grupo', path: `/groups/${groupId}` },
          { label: 'Asistencia del juego' }
        ]}
        backPath={`/groups/${groupId}`}
      >
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          Cargando asistencia del juego...
        </div>
      </DetailLayout>
    );
  }

  return (
    <DetailLayout
      title={juego.nombre || `Juego del ${group?.nombre_del_grupo || ''}`}
      breadcrumbs={[
        { label: 'Grupos', path: '/groups' },
        { label: group?.nombre_del_grupo || 'Grupo', path: `/groups/${groupId}` },
        { label: 'Asistencia del juego' }
      ]}
      backPath={`/groups/${groupId}`}
    >
      <div className="space-y-4">
        <Card>
            <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-xl">{juego.nombre || `vs ${opponent}`}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {torneoNombre ? `Torneo: ${torneoNombre}` : 'Partido amistoso'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {location.state?.focusTorneoId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      navigate(`/torneos/${location.state.focusTorneoId}`, {
                        state: {
                          focusGroupId: groupId,
                          focusTorneoId: location.state.focusTorneoId
                        }
                      })
                    }
                  >
                    Ver torneo
                  </Button>
                )}
                {/* Mismo badge que JuegoCard: un solo lenguaje para "este partido no lo
                    lleva el club, lo lleva la organizadora". */}
                {!esMio && (
                  <Badge variant="outline" className="text-[10px] gap-1 border-blue-500/30 bg-blue-500/10 text-blue-700">
                    <Globe2 className="h-3 w-3" /> Rol oficial
                  </Badge>
                )}
                <Badge
                  variant="outline"
                  className={`text-xs uppercase ${STATUS_COLORS[statusKey] || STATUS_COLORS.programado}`}
                >
                  {STATUS_LABELS[statusKey] || juego.estatus || 'Programado'}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {juego.fecha
                    ? parseLocalDate(juego.fecha)?.toLocaleDateString('es-ES', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short'
                      })
                    : 'Fecha por definir'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{juego.hora ? juego.hora.slice(0, 5) : 'Hora por definir'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                <span>{opponent}</span>
              </div>
              {juego.ubicacion && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span className="truncate">{juego.ubicacion}</span>
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <StatChip label="Presentes" value={stats.presente} tone="success" />
              <StatChip label="Retardos" value={stats.retardo} tone="warning" />
              <StatChip label="Ausentes" value={stats.ausente} tone="danger" />
              <StatChip label="Exentos" value={stats.exento} tone="default" />
              <StatChip label="Pendientes" value={stats.pendientes} />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={saving || !isEditable}
                className="text-green-600 border-green-600 hover:bg-green-50"
                onClick={() => markAll('presente')}
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Todos presentes
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={saving || !isEditable}
                className="text-red-600 border-red-600 hover:bg-red-50"
                onClick={() => markAll('ausente')}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Todos ausentes
              </Button>
              {esMio && (
                <>
                  {isEditable ? (
                    <Button size="sm" onClick={finishGameAttendance} disabled={finishing}>
                      {finishing ? 'Procesando...' : 'Terminar juego'}
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={reactivateGame}>
                      Reactivar juego
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canEditResultado}
                    onClick={() => setShowScoreModal(true)}
                  >
                    Editar marcador
                  </Button>
                </>
              )}
            </div>

            {!esMio && (
              <p className="text-xs text-muted-foreground">
                El marcador y el estado de este juego los lleva la organizadora del torneo.
              </p>
            )}
          </CardContent>
        </Card>

        {players.length > 5 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Buscar jugador por nombre, apodo o número..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        )}

        {filteredPlayers.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center text-muted-foreground">
              No hay jugadores para mostrar.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredPlayers.map((player) => {
              const playerState = attendance[player.id] || 'pendiente';
              return (
                <Card
                  key={player.id}
                  className={`relative transition-all ${
                    !isEditable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                  } ${
                    playerState === 'presente'
                      ? 'ring-2 ring-green-500 bg-green-50 hover:bg-green-100'
                      : playerState === 'retardo'
                        ? 'ring-2 ring-amber-500 bg-amber-50 hover:bg-amber-100'
                      : playerState === 'ausente'
                          ? 'ring-2 ring-rose-400 bg-rose-50 hover:bg-rose-100'
                          : playerState === 'exento'
                            ? 'ring-2 ring-slate-400 bg-slate-50 hover:bg-slate-100'
                            : 'hover:bg-muted/50'
                  }`}
                  onClick={() => {
                    if (saving || !isEditable) return;
                    cycleAttendance(player.id);
                  }}
                >
                  <div className="absolute top-2 right-2">
                    {playerState === 'presente' ? (
                      <div className="bg-green-600 text-white rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                    ) : playerState === 'retardo' ? (
                      <div className="bg-amber-500 text-white rounded-full p-1">
                        <Clock className="h-3 w-3" />
                      </div>
                    ) : playerState === 'ausente' ? (
                      <div className="bg-rose-500 text-white rounded-full p-1">
                        <X className="h-3 w-3" />
                      </div>
                    ) : playerState === 'exento' ? (
                      <div className="bg-slate-500 text-white rounded-full p-1">
                        <Minus className="h-3 w-3" />
                      </div>
                    ) : (
                      <div className="bg-slate-300 text-white rounded-full p-1">
                        <Users className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                  <CardContent className="pt-6 pb-4 flex flex-col items-center text-center space-y-2">
                    <PlayerAvatar
                      imagen_url={player.imagen_url}
                      nombre_completo={player.nombre_completo}
                      size="lg"
                    />
                    <div className="text-sm font-semibold line-clamp-2">
                      {player.nombre_completo}
                    </div>
                    {player.nickname && (
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        "{player.nickname}"
                      </p>
                    )}
                    <Badge
                      variant={
                        playerState === 'presente'
                          ? 'default'
                          : playerState === 'retardo'
                            ? 'secondary'
                            : playerState === 'ausente'
                              ? 'outline'
                              : 'secondary'
                      }
                      className={
                        playerState === 'presente'
                          ? 'bg-green-600 hover:bg-green-700'
                          : playerState === 'retardo'
                            ? 'bg-amber-500 hover:bg-amber-600'
                          : playerState === 'ausente'
                              ? 'text-rose-600 border-rose-600'
                              : playerState === 'exento'
                                ? 'text-slate-600 border-slate-400'
                                : 'text-slate-600 border-dashed'
                      }
                    >
                      {playerState === 'presente'
                        ? 'Presente'
                        : playerState === 'retardo'
                          ? 'Retardo'
                        : playerState === 'ausente'
                            ? 'Ausente'
                            : playerState === 'exento'
                              ? 'Exento'
                              : 'Pendiente'}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <JuegoScoreModal
          isOpen={showScoreModal}
          juego={juego}
          onClose={() => setShowScoreModal(false)}
          onSaved={handleScoreSaved}
        />
      </div>

      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        showCancel={modal.showCancel}
      />
    </DetailLayout>
  );
};

const StatChip = ({ label, value, tone = 'default' }) => {
  const toneClasses = {
    default: 'bg-slate-50 text-slate-700 border-slate-200',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    danger: 'bg-rose-50 text-rose-700 border-rose-200'
  };

  return (
    <Card className={`border shadow-none ${toneClasses[tone] || toneClasses.default}`}>
      <CardContent className="py-3 px-4 flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
};

export default GameAttendanceScreen;



